import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import { useRolePermissions, filterUsersByRole } from '../hooks/useRolePermissions';
import PageHeader from './PageHeader';

export default function Contacts({ userRole, currentUserId }) {
  const navigate = useNavigate();
  
  // Role permissions
  const { permissions, hasPermission } = useRolePermissions(userRole);
  
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/users?userId=${currentUserId || ''}`)
      .then(res => res.json())
      .then(data => {
        // Filtruj uživatele podle role permissions
        const visibleUsers = hasPermission('viewAllUsers') 
          ? data 
          : data.filter(user => user._id === currentUserId);
        
        setUsers(visibleUsers);
        setFilteredUsers(visibleUsers);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching users:', error);
        setLoading(false);
      });
  }, [hasPermission, currentUserId]);

  // Filtrování uživatelů
  useEffect(() => {
    let filtered = [...users];
    
    // Filtr podle kanceláře
    if (selectedOffice) {
      filtered = filtered.filter(user => user.office === selectedOffice);
    }
    
    // Filtr podle jména
    if (nameFilter.trim()) {
      const query = nameFilter.toLowerCase();
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
    }
    
    setFilteredUsers(filtered);
  }, [users, selectedOffice, nameFilter]);

  // Získání unikátních kanceláří
  const uniqueOffices = [...new Set(users.map(user => user.office).filter(Boolean))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 dark:from-gray-800 dark:to-gray-900 py-10">
      <div className="w-[98vw] max-w-[1800px] mx-auto px-4">
        <PageHeader
          title="👥 Kontakty obchodníků"
        />
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            {/* Filtry */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Filtr podle jména */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vyhledat</label>
                <input
                  type="text"
                  placeholder="Jméno nebo email..."
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm w-48"
                />
              </div>
              
              {/* Filtr podle kanceláře */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kancelář</label>
                <select
                  value={selectedOffice}
                  onChange={(e) => setSelectedOffice(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                >
                  <option value="">Všechny kanceláře</option>
                  {uniqueOffices.map(office => (
                    <option key={office} value={office}>
                      {office}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400">Načítám kontakty...</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg shadow" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr className="bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100">
                    <th className="py-3 px-4 text-left font-semibold" style={{ width: '22%' }}>Jméno</th>
                    <th className="py-3 px-4 text-left font-semibold" style={{ width: '12%' }}>VF Login</th>
                    <th className="py-3 px-4 text-left font-semibold" style={{ width: '10%' }}>Role</th>
                    <th className="py-3 px-4 text-left font-semibold" style={{ width: '16%' }}>Telefon</th>
                    <th className="py-3 px-4 text-left font-semibold" style={{ width: '22%' }}>Email</th>
                    <th className="py-3 px-4 text-left font-semibold" style={{ width: '18%' }}>Kancelář</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-gray-500 dark:text-gray-400 py-8 text-center">
                        <div className="text-4xl mb-2">👤</div>
                        <div>
                          {nameFilter || selectedOffice 
                            ? "Žádní obchodníci odpovídající kritériím" 
                            : "Žádní obchodníci"
                          }
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user, index) => (
                      <tr 
                        key={user._id} 
                        className="border-t border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300 rounded-full flex items-center justify-center font-bold text-sm">
                              {user.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {user.name || '-'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-700 dark:text-gray-300 font-mono">
                            {user.vfLogin || <span className="text-gray-400 dark:text-gray-500">-</span>}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.role === 'admin' 
                              ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                              : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300'
                          }`}>
                            {user.role || "obchodník"}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {user.phone ? (
                            <a 
                              href={`tel:${user.phone}`}
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {user.phone}
                            </a>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">neuveden</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {user.email ? (
                            <a 
                              href={`mailto:${user.email}`}
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {user.email}
                            </a>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">neuveden</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {user.office ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-sm">
                              🏢 {user.office}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">neuvedeno</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}